/**
 * 登録済みクライアントIDの編集（名前・環境・クライアントID本体）。
 *
 * クライアントID本体はマスク表示のみで取得できないため、入力欄は空から始める。
 * 空のまま保存したら「変更しない」の意味にする（マスク値を送り返すと
 * それが本物として保存されてしまう）。
 *
 * クライアントIDと環境の変更は、進行中の書類に影響する。
 * クラウドサインの書類は発行元アカウントと環境に紐づくため、別の値に変えると
 * それまでの書類をポーリングが取得できなくなる（not_found になる）。
 * 画面で必ず警告する。
 */
import { useState } from "react";
import { api } from "../api.js";
import { labelStyle, inputStyle, selectStyle, btnPrimary, btnSecondary } from "./ui.js";

export interface ClientIdRow {
  id: string;
  label: string;
  environment: string;
  maskedClientId: string;
}

export default function ClientIdEditForm({
  row,
  onSaved,
  onCancel,
}: {
  row: ClientIdRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(row.label);
  const [environment, setEnvironment] = useState(row.environment);
  const [clientId, setClientId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientIdChanged = clientId.trim() !== "";
  const environmentChanged = environment !== row.environment;
  /** 進行中の書類に影響する変更をしているか */
  const risky = clientIdChanged || environmentChanged;

  const save = async () => {
    setError(null);

    if (clientIdChanged && !/^[a-f0-9-]{36}$/i.test(clientId.trim())) {
      setError("クライアントIDは36桁の英数字・ハイフンです。");
      return;
    }
    if (!label.trim()) {
      setError("名前を入力してください。");
      return;
    }
    if (risky) {
      const what = [
        clientIdChanged ? "クライアントID" : null,
        environmentChanged ? "環境" : null,
      ].filter(Boolean).join("と");
      const ok = confirm(
        `${what}を変更します。\n\n` +
          "このクライアントIDで送信済みの書類は、クラウドサイン側で参照できなくなる可能性があります" +
          "（ステータス同期・締結完了ファイルの取得が失敗します）。\n\n続行しますか？",
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      await api.updateClientId(row.id, {
        label: label.trim(),
        environment,
        // 空欄は「変更しない」。マスク値を送り返さないため
        ...(clientIdChanged ? { clientId: clientId.trim() } : {}),
      } as any);
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 12, padding: 16, border: "1px solid #d0cfc9", borderRadius: 10, background: "#fbfaf8" }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>クライアントIDの編集</div>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>名前</div>
        <input
          style={inputStyle}
          type="text"
          value={label}
          maxLength={255}
          placeholder="例: 営業部"
          onChange={(e) => setLabel(e.target.value)}
        />
        <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 4 }}>
          送信モーダルの選択肢に表示される名前です。送信には影響しません。
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>環境</div>
        <select style={selectStyle} value={environment} onChange={(e) => setEnvironment(e.target.value)}>
          <option value="sandbox">サンドボックス（sandbox.cloudsign.jp）</option>
          <option value="production">本番環境（api.cloudsign.jp）</option>
        </select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={labelStyle}>クライアントID</div>
        <div style={{ fontSize: 13, color: "#6b6b68", fontFamily: "monospace", marginBottom: 6 }}>
          現在: {row.maskedClientId}
        </div>
        <input
          style={inputStyle}
          type="text"
          value={clientId}
          placeholder="変更する場合のみ入力（36桁）"
          onChange={(e) => setClientId(e.target.value)}
        />
        <div style={{ fontSize: 12, color: "#6b6b68", marginTop: 4 }}>
          保存済みの値は表示できません。<b>空欄のままにすると変更しません。</b>
        </div>
      </div>

      {risky && (
        <div style={{ padding: "12px 16px", background: "#FAEEDA", borderLeft: "3px solid #BA7517", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#854F0B", lineHeight: 1.8, marginBottom: 14 }}>
          <b>進行中の書類に影響します。</b>
          <br />
          クラウドサインの書類は発行元アカウントと環境に紐づきます。
          このクライアントIDで送信済みの書類は、変更後に参照できなくなる可能性があります
          （ステータスが同期されず、締結完了ファイルも取得できません）。
          <br />
          保存後は「接続テスト」で疎通を確認してください。
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button style={{ ...btnPrimary, fontSize: 13, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
          {saving ? "保存中..." : "保存"}
        </button>
        <button style={{ ...btnSecondary, fontSize: 13 }} disabled={saving} onClick={onCancel}>
          キャンセル
        </button>
      </div>
    </div>
  );
}
